import { Injectable } from '@angular/core';

/**
 * Tracks which deliverables currently have at least one field whose value genuinely differs from
 * its value at the start of the campaign-form session, so the `_saved` baseline-diff mechanism
 * (campaign-form.component.ts) can tell "changed_saved" apart from "untouched" without raw
 * value-diffing at save time (see docs/event-tracking-implementation-map.md, "Deliverable _saved
 * event design").
 *
 * Tracked per-field (not just per-deliverable) so an edit that's later reverted back to its
 * original value correctly stops counting as "changed" - a plain per-deliverable dirty flag would
 * stay set forever after the first edit, even if the user types something then undoes it before
 * saving, which would fire a `changed_saved` event for a deliverable that nets out unchanged. That
 * would violate the doc's own "never fire a _saved event the user didn't actually cause" rule in
 * substance, even though the user did technically touch the field at some point.
 *
 * Marked/unmarked from custom-values.service.ts's debounced field-change tracking subscription
 * (the same commit point that fires #8/#9), which only fires on genuine emitted valueChanges - the
 * cascading-write mode-toggle rewrites in that same service use `{ emitEvent: false }` and never
 * reach here, so this can't be spuriously dirtied by anything other than a real user edit.
 *
 * providedIn: 'root' (singleton for the app) - must be reset by campaign-form.component.ts after
 * establishing/re-establishing its baseline, so state from a previous campaign-form session or a
 * previous save never leaks into the next diff.
 */
@Injectable({
  providedIn: 'root',
})
export class DeliverableChangeTrackerService {
  // deliverableId -> set of field names currently net-different from their session-start value.
  private readonly dirtyFieldsByDeliverable = new Map<string, Set<string>>();
  // deliverableId -> set of nested-array-item sources (e.g. "text_4") added this session that
  // haven't been through a save yet. Lets a same-session add-then-remove of the same row cancel
  // out instead of leaving a false-positive dirty flag - see markFieldChanged's own doc comment on
  // why per-field (not per-deliverable) tracking exists; this is the equivalent fix for structural
  // add/remove, which has no scalar "original value" to compare against (found in manual QA
  // 2026-08-03's review of issue #3).
  private readonly itemsAddedThisSessionByDeliverable = new Map<string, Set<string>>();

  public markFieldChanged(deliverableId: string | undefined, fieldName: string): void {
    if (!deliverableId) {
      return;
    }

    const fields = this.dirtyFieldsByDeliverable.get(deliverableId) ?? new Set<string>();

    fields.add(fieldName);
    this.dirtyFieldsByDeliverable.set(deliverableId, fields);
  }

  public markFieldUnchanged(deliverableId: string | undefined, fieldName: string): void {
    if (!deliverableId) {
      return;
    }

    const fields = this.dirtyFieldsByDeliverable.get(deliverableId);

    fields?.delete(fieldName);

    if (fields?.size === 0) {
      this.dirtyFieldsByDeliverable.delete(deliverableId);
    }
  }

  public isDirty(deliverableId: string): boolean {
    return !!this.dirtyFieldsByDeliverable.get(deliverableId)?.size;
  }

  public markItemAddedThisSession(deliverableId: string | undefined, source: string): void {
    if (!deliverableId) {
      return;
    }

    const sources = this.itemsAddedThisSessionByDeliverable.get(deliverableId) ?? new Set<string>();

    sources.add(source);
    this.itemsAddedThisSessionByDeliverable.set(deliverableId, sources);
  }

  // Also evicts the entry, since a source can only cancel out once - re-adding the same source
  // afterward (e.g. add, remove, add again) correctly starts a fresh "added this session" record.
  public consumeItemAddedThisSession(deliverableId: string | undefined, source: string): boolean {
    if (!deliverableId) {
      return false;
    }

    const sources = this.itemsAddedThisSessionByDeliverable.get(deliverableId);
    const wasAddedThisSession = sources?.has(source) ?? false;

    sources?.delete(source);

    if (sources?.size === 0) {
      this.itemsAddedThisSessionByDeliverable.delete(deliverableId);
    }

    return wasAddedThisSession;
  }

  public reset(): void {
    this.dirtyFieldsByDeliverable.clear();
    this.itemsAddedThisSessionByDeliverable.clear();
  }
}
